// frontend/src/api/hooks/useDimValues.ts
//
// Returns the distinct values present for a given dimension on a given fact_table,
// derived from the `dims_view` field of the controls bundle.
//
// Schema: see frontend/src/api/endpoints/getControls.ts. dims_view is now strictly
// validated by zod (Batch F audit #5+#6 fix); a backend rename of dims_view[].dims
// would fail loudly at parse time rather than silently producing empty dropdowns.
//
// The `kpi` parameter is part of the public signature so callers can opt into
// kpi-scoped dim values in the future, but it is currently unused for filtering:
// the FE's `kpi` is the encoded id (e.g. `model_sales__media__units`) while
// `dims_view.kpi_type` is the kpi root (e.g. `brand_recognition`), so a naive
// equality match would over-filter.

import { useMemo } from 'react'
import { usePageControls } from './useCharts'
import { compareLabel } from '~/util/compareLabel'

export function useDimValues(dim: string, factTable: string, _kpi: string) {
  const controls = usePageControls(factTable)

  const data = useMemo(() => {
    if (!dim) return []
    const rows = controls.data?.dims_view ?? []
    const seen = new Set<string>()
    const ordered: string[] = []
    for (const row of rows) {
      const v = row.dims?.[dim]
      if (v === undefined || v === null) continue
      if (!seen.has(v)) {
        seen.add(v)
        ordered.push(v)
      }
    }
    return ordered.sort(compareLabel)
  }, [controls.data, dim])

  return {
    ...controls,
    data,
  }
}
